/** The server's error envelope (`{ error, code }`), surfaced as an exception. */
export class HubApiError extends Error {
    status;
    code;
    body;
    constructor(status, code, message, body) {
        super(message);
        this.name = 'HubApiError';
        this.status = status;
        this.code = code;
        this.body = body;
    }
}
const resolve = (value) => typeof value === 'function' ? value() : value;
function isBinaryBody(value) {
    if (typeof Blob !== 'undefined' && value instanceof Blob)
        return true;
    return value instanceof ArrayBuffer;
}
export function fillPath(template, params) {
    return template.replace(/\{([^}]+)\}/g, (_match, name) => {
        const value = params?.[name];
        if (value === undefined || value === null)
            throw new Error(`Missing path parameter "${name}" for ${template}`);
        return encodeURIComponent(String(value));
    });
}
export function createHubClient(options) {
    const doFetch = options.fetch ?? globalThis.fetch;
    const apiBase = (options.apiBase ?? '/api/v1').replace(/\/$/, '');
    const origin = options.baseUrl.replace(/\/$/, '');
    async function raw(method, path, init = {}) {
        const url = new URL(`${origin}${apiBase}${fillPath(path, init.params)}`);
        for (const [key, value] of Object.entries(init.query ?? {})) {
            if (value !== undefined && value !== null)
                url.searchParams.set(key, String(value));
        }
        const headers = { Accept: 'application/json', ...init.headers };
        const token = resolve(options.token);
        if (token)
            headers.Authorization = `Bearer ${token}`;
        // A request that names its own workspace keeps it: a row from another workspace (a
        // schedule on the global Schedules page) is acted on where it lives.
        const profile = resolve(options.profile);
        if (profile && !headers['X-Hub-Profile'])
            headers['X-Hub-Profile'] = profile;
        if (options.language)
            headers['Accept-Language'] = options.language;
        let body;
        if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
            // Multipart (attachments): the runtime sets the boundary header itself.
            body = init.body;
        }
        else if (isBinaryBody(init.body)) {
            // Raw bytes (a resumable upload's chunk): never stringified, and the caller's
            // own `Content-Type` stands, because the contract declares one per operation.
            headers['Content-Type'] ??= 'application/octet-stream';
            body = init.body;
        }
        else if (init.body !== undefined) {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(init.body);
        }
        const response = await doFetch(url, {
            method: method.toUpperCase(),
            headers,
            ...(body !== undefined ? { body } : {}),
            ...(init.signal ? { signal: init.signal } : {}),
        });
        // A binary response is only read as bytes when it succeeded: an error is still the
        // `{ error, code }` envelope, and the caller must be able to read it.
        if (init.responseKind === 'bytes' && response.ok) {
            return {
                status: response.status,
                data: (await response.arrayBuffer()),
                headers: response.headers,
            };
        }
        const text = await response.text();
        let data = undefined;
        if (text.length > 0) {
            try {
                data = JSON.parse(text);
            }
            catch {
                data = text;
            }
        }
        if (!response.ok) {
            const envelope = (data ?? {});
            const code = typeof envelope.code === 'string' ? envelope.code : 'http_error';
            const message = typeof envelope.error === 'string' ? envelope.error : `HTTP ${response.status}`;
            throw new HubApiError(response.status, code, message, data);
        }
        return { status: response.status, data, headers: response.headers };
    }
    return {
        request: raw,
        raw,
    };
}
//# sourceMappingURL=client.js.map